import React from 'react'
import { mediaUrl, type Media } from '@/lib/api'

// Class-free Lexical → React renderer for the article page. Output is plain
// semantic HTML so the .article CSS fully controls typography. Headings get
// slug ids so the table of contents can link to them. Keep in sync with
// server/src/site/lexicalHtml.ts, which renders the same markup for crawlers.

interface LexNode {
  type: string
  children?: LexNode[]
  text?: string
  format?: number | string
  tag?: string
  listType?: string
  url?: string
  fields?: Record<string, any>
  value?: Media | string
}
interface LexRoot {
  root: LexNode
}

export const slugify = (s: string): string =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')

const nodeText = (node: LexNode): string => {
  if (typeof node.text === 'string') return node.text
  if (node.children) return node.children.map(nodeText).join('')
  return ''
}

export const extractHeadings = (content: unknown): { id: string; text: string }[] => {
  const root = content as LexRoot
  const kids = root?.root?.children
  if (!kids) return []
  const out: { id: string; text: string }[] = []
  kids.forEach((n) => {
    if (n.type === 'heading' && n.tag === 'h2') {
      const text = nodeText(n).trim()
      if (text) out.push({ id: slugify(text), text })
    }
  })
  return out
}

export const readingTime = (content: unknown): number => {
  const root = content as LexRoot
  if (!root?.root) return 0
  const words = nodeText(root.root).trim().split(/\s+/).filter(Boolean).length
  return words ? Math.max(1, Math.round(words / 200)) : 0
}

export function videoEmbedUrl(url: string): string | null {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/)
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}`
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/)
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`
  return null
}

function linkAttrs(fields: Record<string, any> | undefined, fallbackUrl?: string) {
  let href = fields?.url || fallbackUrl || '#'
  if (fields?.linkType === 'internal') {
    const doc = fields.doc?.value
    href = doc && typeof doc === 'object' && doc.slug ? `/${doc.slug}/` : '#'
  }
  const rel: string[] = Array.isArray(fields?.rel) ? [...fields!.rel] : []
  if (fields?.newTab) rel.push('noopener', 'noreferrer')
  return { href, target: fields?.newTab ? '_blank' : undefined, rel: rel.length ? rel.join(' ') : undefined }
}

function renderBlock(fields: Record<string, any>, key: number): React.ReactNode {
  switch (fields.blockType) {
    case 'code':
      return (
        <pre key={key} data-lang={fields.language || undefined}>
          <code>{fields.code}</code>
        </pre>
      )
    case 'video': {
      const src = videoEmbedUrl(String(fields.url || ''))
      if (!src) return null
      return (
        <div key={key} className="video-embed">
          <iframe
            src={src}
            title={fields.title}
            loading="lazy"
            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      )
    }
    case 'html':
      return <div key={key} dangerouslySetInnerHTML={{ __html: String(fields.html || '') }} />
    default:
      return null
  }
}

const FMT_BOLD = 1
const FMT_ITALIC = 1 << 1
const FMT_STRIKE = 1 << 2
const FMT_UNDERLINE = 1 << 3
const FMT_CODE = 1 << 4
const FMT_SUB = 1 << 5
const FMT_SUP = 1 << 6

function renderText(node: LexNode, key: number) {
  let el: React.ReactNode = node.text ?? ''
  const f = typeof node.format === 'number' ? node.format : 0
  if (f & FMT_CODE) el = <code>{el}</code>
  if (f & FMT_BOLD) el = <strong>{el}</strong>
  if (f & FMT_ITALIC) el = <em>{el}</em>
  if (f & FMT_UNDERLINE) el = <u>{el}</u>
  if (f & FMT_STRIKE) el = <s>{el}</s>
  if (f & FMT_SUB) el = <sub>{el}</sub>
  if (f & FMT_SUP) el = <sup>{el}</sup>
  return <React.Fragment key={key}>{el}</React.Fragment>
}

function renderChildren(nodes: LexNode[] | undefined): React.ReactNode {
  return nodes ? nodes.map((n, i) => renderNode(n, i)) : null
}

function renderNode(node: LexNode, key: number): React.ReactNode {
  switch (node.type) {
    case 'text':
      return renderText(node, key)
    case 'linebreak':
      return <br key={key} />
    case 'paragraph':
      return <p key={key}>{renderChildren(node.children)}</p>
    case 'heading': {
      const Tag = (node.tag || 'h2') as keyof JSX.IntrinsicElements
      const id = slugify(nodeText(node))
      return (
        <Tag key={key} id={id}>
          {renderChildren(node.children)}
        </Tag>
      )
    }
    case 'quote':
      return <blockquote key={key}>{renderChildren(node.children)}</blockquote>
    case 'list': {
      const ordered = node.listType === 'number' || node.tag === 'ol'
      const Tag = ordered ? 'ol' : 'ul'
      return <Tag key={key}>{renderChildren(node.children)}</Tag>
    }
    case 'listitem':
      return <li key={key}>{renderChildren(node.children)}</li>
    case 'horizontalrule':
      return <hr key={key} />
    case 'link':
    case 'autolink': {
      const a = linkAttrs(node.fields, node.url)
      return (
        <a key={key} href={a.href} target={a.target} rel={a.rel}>
          {renderChildren(node.children)}
        </a>
      )
    }
    case 'upload': {
      const media = node.value
      if (!media || typeof media !== 'object' || !media.url) return null
      if (media.mimeType?.startsWith('video/')) {
        return (
          <figure key={key}>
            <video src={mediaUrl(media)} controls preload="metadata" />
          </figure>
        )
      }
      return (
        <figure key={key}>
          <img
            src={mediaUrl(media, 'hero')}
            alt={media.alt || ''}
            width={media.width}
            height={media.height}
            loading="lazy"
          />
          {media.caption && <figcaption>{media.caption}</figcaption>}
        </figure>
      )
    }
    case 'block':
      return renderBlock(node.fields || {}, key)
    default:
      return node.children ? (
        <React.Fragment key={key}>{renderChildren(node.children)}</React.Fragment>
      ) : null
  }
}

export default function ArticleRichText({ content }: { content: unknown }) {
  const root = content as LexRoot
  if (!root?.root?.children) return null
  return <>{renderChildren(root.root.children)}</>
}
